import { useEffect, useRef, useState } from "react";
import { vibrate } from "../hooks.ts";
import { Karta, Komunikat, Odznaka, Przycisk } from "../ui.tsx";

/**
 * Statki — ekran salwy.
 *
 * Główny problem projektowy tej gry to dwie plansze 10×10 na pięciu calach.
 * Rozwiązuje go **asymetria**: plansza celu zajmuje całą szerokość, własna
 * leży w stopce jako miniatura na 110 px i rozwija się dotknięciem. Obie nigdy
 * nie są jednocześnie duże.
 *
 * Pole ma na 390 px około 36 px, a próg wygodnego dotyku to 44 px. Zamiast
 * powiększać pole (plansza byłaby wtedy szersza od ekranu i trzeba by ją
 * przewijać **w trakcie celowania**), rozdzielamy wskazanie od zatwierdzenia:
 * dotknięcie przestawia celownik i nic więcej, a strzela przycisk o wysokości
 * 56 px w stałym miejscu na dole. Chybione dotknięcie kosztuje wtedy jedno
 * kolejne dotknięcie, a nie spalony strzał. Ta sama zasada, co ze stojakiem
 * w Scrabble.
 */

const BOK = 10;
const MAX_SKALA = 3;

export interface StatekWidok {
  rozmiar: number;
  pola: number[];
  trafienia: boolean[];
}

export interface StrzalWidok {
  pole: number;
  strzelec: string;
  wynik: "pudlo" | "trafiony" | "zatopiony";
  salwa: number;
}

interface CelWidok {
  gracz: string;
  kolor: string;
  zywa: boolean;
  strzaly: StrzalWidok[];
  /** Pola statków zatopionych — jedyne pola cudzej floty, które wolno pokazać. */
  zatopione: number[];
  pewnaWoda: number[];
  pozostaleRozmiary: number[];
  masztow: number;
  celeStrzalu: number[];
  /** Wyłącznie w fazie `koniec`: pełna flota, odkryta dla wszystkich. */
  statki?: StatekWidok[];
}

export interface StatkiView {
  phase: "lobby" | "rozstawianie" | "salwa" | "koniec";
  faza: "lobby" | "rozstawianie" | "salwa" | "koniec";
  hostId: string;
  players: string[];
  kolory: Record<string, string>;
  canStart: boolean;
  numerSalwy: number;
  deadlineAt: number | null;
  flota: number[];
  masztowWeFlocie: number;
  ustawienia: {
    flota: string;
    statkiMogaSieStykac: boolean;
    strzalZaTrafienie: boolean;
    maxStrzalowWSalwie: number;
    pokazujPewnaWode: boolean;
  };
  mojaPlansza: {
    statki: StatekWidok[];
    strzaly: StrzalWidok[];
    zatopione: number[];
    gotowa: boolean;
    zywa: boolean;
    masztow: number;
    padlaWSalwie: number | null;
  } | null;
  odpadl: boolean;

  przeciwnicy?: Array<{ gracz: string; gotowa: boolean }>;
  celeRozstawienia?: Array<{ rozmiar: number; kotwice: Array<{ pole: number; pionowo: boolean }> }>;
  doPostawienia?: number[];

  cele?: CelWidok[];
  tabela?: Array<{
    gracz: string;
    zywa: boolean;
    masztow: number;
    zatopil: number;
    oddanych: number;
    trafionych: number;
    padlaWSalwie: number | null;
  }>;
  mojeStrzaly?: Array<{ cel: string; pole: number }>;
  strzalowWSalwie?: number;
  iluWystrzelilo?: number;
  iluZywych?: number;
  punkty?: Record<string, number> | null;
}

/** „C4" — litera z wiersza, cyfra z kolumny. Powstaje dopiero na ekranie. */
export function nazwaPola(pole: number): string {
  return `${String.fromCharCode(65 + Math.floor(pole / BOK))}${(pole % BOK) + 1}`;
}

/**
 * Liczebniki porządkowe zamiast cyfry w nawiasie.
 *
 * `maxStrzalowWSalwie` ma zakres 1–5, więc lista jest kompletna z definicji;
 * gdyby kiedyś urosła, wpis poza zakresem wraca do gołego „Ognia".
 */
const KTORY_STRZAL = ["", "Pierwszy", "Drugi", "Trzeci", "Czwarty", "Piąty"];
const KTORE_POLE = ["", "pierwsze", "drugie", "trzecie", "czwarte", "piąte"];

/** „1 strzał", „2 strzały", „5 strzałów" — polska odmiana, nie „strzał(ów)". */
export function odmianaStrzalow(ile: number): string {
  if (ile === 1) return "1 strzał";
  const dziesiatki = ile % 100;
  const jednosci = ile % 10;
  const mnoga = jednosci >= 2 && jednosci <= 4 && (dziesiatki < 12 || dziesiatki > 14);
  return `${ile} ${mnoga ? "strzały" : "strzałów"}`;
}

/** Orzeczenie do `odmianaStrzalow`: został 1 strzał, zostały 2, zostało 5. */
export function czasownikZostalo(ile: number): string {
  if (ile === 1) return "został";
  const dziesiatki = ile % 100;
  const jednosci = ile % 10;
  return jednosci >= 2 && jednosci <= 4 && (dziesiatki < 12 || dziesiatki > 14)
    ? "zostały"
    : "zostało";
}

const NAZWY_ROZMIARU: Record<number, string> = {
  4: "Czteromasztowiec",
  3: "Trójmasztowiec",
  2: "Dwumasztowiec",
  1: "Jednomasztowiec",
};

export function Statki({
  view,
  nicki,
  mojeId,
  onStart,
  onPostaw,
  onZdejmij,
  onLosuj,
  onGotow,
  onCofnijGotow,
  onStrzel,
  onCofnijStrzal,
}: {
  view: StatkiView;
  nicki: Record<string, string>;
  mojeId: string;
  onStart: () => void;
  onPostaw: (rozmiar: number, pole: number, pionowo: boolean) => void;
  onZdejmij: (pole: number) => void;
  onLosuj: () => void;
  onGotow: () => void;
  onCofnijGotow: () => void;
  onStrzel: (cel: string, pole: number) => void;
  onCofnijStrzal: () => void;
}) {
  if (view.faza === "lobby") return <Lobby view={view} nicki={nicki} onStart={onStart} />;

  if (view.faza === "rozstawianie") {
    return (
      <Rozstawianie
        view={view}
        nicki={nicki}
        onPostaw={onPostaw}
        onZdejmij={onZdejmij}
        onLosuj={onLosuj}
        onGotow={onGotow}
        onCofnijGotow={onCofnijGotow}
      />
    );
  }

  if (view.faza === "koniec") return <Koniec view={view} nicki={nicki} mojeId={mojeId} />;

  return (
    <Salwa
      view={view}
      nicki={nicki}
      mojeId={mojeId}
      onStrzel={onStrzel}
      onCofnijStrzal={onCofnijStrzal}
    />
  );
}

function Lobby({
  view,
  nicki,
  onStart,
}: {
  view: StatkiView;
  nicki: Record<string, string>;
  onStart: () => void;
}) {
  return (
    <>
      <Karta>
        <div className="text-sm text-tekst-slaby mb-2">Gracze</div>
        <div className="space-y-1">
          {view.players.map((id) => (
            <div key={id} className="flex justify-between">
              <span>{nicki[id] ?? id}</span>
              {id === view.hostId ? <Odznaka>host</Odznaka> : null}
            </div>
          ))}
        </div>
      </Karta>
      <Komunikat>
        Każdy rozstawia flotę na swojej planszy 10×10. Potem strzelacie{" "}
        <strong>jednocześnie</strong> — jedna salwa to jeden strzał w dowolną cudzą planszę, plus
        bonus za trafienia z salwy poprzedniej. Wygrywa ostatnia flota na wodzie.
      </Komunikat>
      {view.canStart ? (
        <Przycisk wariant="glowny" pelna onClick={onStart}>
          Zaczynamy
        </Przycisk>
      ) : (
        <Komunikat>Czekamy, aż host zacznie partię.</Komunikat>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Rozstawianie
// ---------------------------------------------------------------------------

function Rozstawianie({
  view,
  nicki,
  onPostaw,
  onZdejmij,
  onLosuj,
  onGotow,
  onCofnijGotow,
}: {
  view: StatkiView;
  nicki: Record<string, string>;
  onPostaw: (rozmiar: number, pole: number, pionowo: boolean) => void;
  onZdejmij: (pole: number) => void;
  onLosuj: () => void;
  onGotow: () => void;
  onCofnijGotow: () => void;
}) {
  const doPostawienia = view.doPostawienia ?? [];
  const cele = view.celeRozstawienia ?? [];
  const [rozmiar, setRozmiar] = useState<number | null>(null);
  const [pionowo, setPionowo] = useState(false);
  const gotowa = view.mojaPlansza?.gotowa === true;

  // Rozmiar, którego już nie ma w puli, nie może zostać zaznaczony — inaczej
  // plansza świeciłaby kotwicami dla statku, którego nie da się postawić.
  const wybrany = rozmiar !== null && doPostawienia.includes(rozmiar) ? rozmiar : null;
  const kotwice =
    wybrany === null
      ? []
      : (cele.find((wpis) => wpis.rozmiar === wybrany)?.kotwice ?? []).filter(
          (kotwica) => kotwica.pionowo === pionowo || wybrany === 1,
        );

  const dozwolone = new Set(kotwice.map((kotwica) => kotwica.pole));
  const kompletna = doPostawienia.length === 0;

  return (
    <>
      <Karta>
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm text-tekst-slaby">
            {gotowa
              ? "Flota zgłoszona — czekamy na resztę"
              : kompletna
                ? "Cała flota stoi"
                : `Do rozstawienia: ${doPostawienia.length}`}
          </div>
          <div className="flex gap-2">
            {(view.przeciwnicy ?? []).map((wpis) => (
              <Odznaka key={wpis.gracz} aktywna={wpis.gotowa}>
                {nicki[wpis.gracz] ?? wpis.gracz}
              </Odznaka>
            ))}
          </div>
        </div>
      </Karta>

      <PlanszaSVG
        wlasna
        statki={view.mojaPlansza?.statki ?? []}
        strzaly={[]}
        zatopione={[]}
        pewnaWoda={[]}
        dozwolone={dozwolone}
        celownik={null}
        numerSalwy={0}
        onPole={(pole) => {
          if (gotowa) return;
          if (wybrany !== null && dozwolone.has(pole)) {
            onPostaw(wybrany, pole, wybrany === 1 ? false : pionowo);
            vibrate(20);
            return;
          }
          // Dotknięcie kadłuba zdejmuje statek — serwer sam znajdzie, który to.
          if ((view.mojaPlansza?.statki ?? []).some((statek) => statek.pola.includes(pole))) {
            onZdejmij(pole);
          }
        }}
      />

      {gotowa ? (
        <Przycisk pelna onClick={onCofnijGotow}>
          Jeszcze poprawię
        </Przycisk>
      ) : (
        <>
          <Karta>
            <div className="text-sm text-tekst-slaby mb-2">
              Wybierz statek, potem dotknij pola. Statki nie mogą się stykać
              {view.ustawienia.statkiMogaSieStykac ? " — w tej partii mogą" : ""}.
            </div>
            <div className="flex flex-wrap gap-2">
              {[4, 3, 2, 1].map((r) => {
                const ile = doPostawienia.filter((wpis) => wpis === r).length;
                if (ile === 0) return null;
                return (
                  <Przycisk
                    key={r}
                    wariant={wybrany === r ? "glowny" : "zwykly"}
                    onClick={() => setRozmiar(r)}
                  >
                    {NAZWY_ROZMIARU[r]} ×{ile}
                  </Przycisk>
                );
              })}
              {doPostawienia.length === 0 ? (
                <span className="text-tekst-slaby">Wszystko rozstawione.</span>
              ) : null}
            </div>
            {wybrany !== null && wybrany > 1 ? (
              <div className="mt-3">
                <Przycisk pelna onClick={() => setPionowo(!pionowo)}>
                  {pionowo ? "Pionowo ↓" : "Poziomo →"}
                </Przycisk>
              </div>
            ) : null}
          </Karta>

          <div className="grid grid-cols-2 gap-2">
            <Przycisk onClick={onLosuj}>Rozstaw losowo</Przycisk>
            <Przycisk wariant="glowny" disabled={!kompletna} onClick={onGotow}>
              Gotów
            </Przycisk>
          </div>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Salwa
// ---------------------------------------------------------------------------

function Salwa({
  view,
  nicki,
  mojeId,
  onStrzel,
  onCofnijStrzal,
}: {
  view: StatkiView;
  nicki: Record<string, string>;
  mojeId: string;
  onStrzel: (cel: string, pole: number) => void;
  onCofnijStrzal: () => void;
}) {
  const cele = view.cele ?? [];
  const zywe = cele.filter((cel) => cel.zywa);
  const [aktywny, setAktywny] = useState<string>(zywe[0]?.gracz ?? cele[0]?.gracz ?? "");
  /** Celownik per plansza: przełączenie celu wraca tam, gdzie celowałeś. */
  const [celowniki, setCelowniki] = useState<Record<string, number>>({});
  const [rozwinieta, setRozwinieta] = useState(false);

  // Cel, który właśnie poszedł na dno, przestaje być celem — bez tego przycisk
  // „Ognia" świeciłby nad martwą planszą aż do ręcznego przełączenia.
  useEffect(() => {
    if (zywe.length > 0 && !zywe.some((cel) => cel.gracz === aktywny)) {
      setAktywny(zywe[0]?.gracz ?? "");
    }
  }, [zywe.map((cel) => cel.gracz).join(","), aktywny]);

  const cel = cele.find((wpis) => wpis.gracz === aktywny) ?? null;
  const celownik = cel ? (celowniki[cel.gracz] ?? null) : null;
  const zlozone = view.mojeStrzaly ?? [];
  const limit = view.strzalowWSalwie ?? 0;
  const zostalo = Math.max(0, limit - zlozone.length);

  /**
   * Numer kolejności strzału na danej planszy. `mojeStrzaly` idzie w kolejności
   * składania przez całą salwę, więc numer bierze się z indeksu w tej liście,
   * a nie z pozycji wśród strzałów w jedną planszę — inaczej dwa strzały
   * w dwie plansze byłyby oba „pierwsze".
   */
  const zlozoneNa = (gracz: string) =>
    zlozone.flatMap((strzal, i) =>
      strzal.cel === gracz ? [{ pole: strzal.pole, numer: i + 1 }] : [],
    );

  const naTejPlanszy = cel ? zlozoneNa(cel.gracz) : [];
  /*
   * Pole, w które sam już wycelowałem w tej salwie, wypada z listy dozwolonych.
   * Serwer trzyma je w `celeStrzalu` (rozliczenia jeszcze nie było), ale drugi
   * własny strzał w to samo pole odrzuca — bez tego filtra przycisk „Ognia"
   * świeciłby nad polem, które zwróci błąd.
   */
  const juzWycelowane = new Set(naTejPlanszy.map((wpis) => wpis.pole));
  const dozwolone = new Set(
    (cel?.celeStrzalu ?? []).filter((pole) => !juzWycelowane.has(pole)),
  );

  const etykietaOgnia =
    celownik === null
      ? limit > 1 && KTORE_POLE[zlozone.length + 1]
        ? `Wskaż ${KTORE_POLE[zlozone.length + 1]} pole`
        : "Wskaż pole"
      : limit > 1 && KTORY_STRZAL[zlozone.length + 1]
        ? `${KTORY_STRZAL[zlozone.length + 1]} strzał — ognia w ${nazwaPola(celownik)}`
        : `Ognia w ${nazwaPola(celownik)}`;

  return (
    <>
      {/* Wybór celu przyklejony u góry — pod nim przewija się plansza. */}
      <div className="sticky top-0 z-10 -mx-4 border-b border-obrys bg-tlo px-4 pb-2 pt-2">
        <div className="flex gap-2 overflow-x-auto">
          {cele.map((wpis) => {
            // Ile strzałów tej salwy poszło już w tę planszę — inaczej przy
            // dwóch przeciwnikach gracz nie widzi, że drugi strzał złożył
            // gdzie indziej, i szuka go na planszy, na którą właśnie patrzy.
            const wycelowane = zlozoneNa(wpis.gracz).length;
            return (
              <button
                key={wpis.gracz}
                onClick={() => setAktywny(wpis.gracz)}
                disabled={!wpis.zywa}
                className={[
                  "min-h-14 flex-1 min-w-24 rounded-2xl border px-3 text-left",
                  wpis.gracz === aktywny ? "border-akcent bg-panel-jasny" : "border-obrys",
                  wpis.zywa ? "" : "opacity-40",
                ].join(" ")}
              >
                <span className="flex items-center gap-2">
                  <span
                    className="inline-block h-3 w-3 rounded-full"
                    style={{ backgroundColor: `var(--akcent-${wpis.kolor})` }}
                  />
                  <span className="truncate">{nicki[wpis.gracz] ?? wpis.gracz}</span>
                </span>
                <span className="block text-sm text-tekst-slaby tabular">
                  {wpis.zywa ? `${wpis.masztow} masztów` : "zatopiona"}
                </span>
                {wycelowane > 0 ? (
                  <span
                    className="mt-0.5 inline-block rounded-md border border-dashed px-1.5 text-xs tabular"
                    style={{ borderColor: "var(--strzal-zlozony)", color: "var(--strzal-zlozony)" }}
                  >
                    {odmianaStrzalow(wycelowane)} tutaj
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {view.odpadl ? (
        <Komunikat>
          Twoja flota poszła na dno w {view.mojaPlansza?.padlaWSalwie ?? "?"}. salwie. Zostajesz
          w partii jako kibic — cudzych flot nie podejrzysz.
        </Komunikat>
      ) : null}

      {rozwinieta ? (
        <Karta>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-tekst-slaby">Twoja plansza</span>
            <Przycisk wariant="cichy" onClick={() => setRozwinieta(false)}>
              Zwiń
            </Przycisk>
          </div>
          <PlanszaSVG
            wlasna
            statki={view.mojaPlansza?.statki ?? []}
            strzaly={view.mojaPlansza?.strzaly ?? []}
            zatopione={view.mojaPlansza?.zatopione ?? []}
            pewnaWoda={[]}
            dozwolone={null}
            celownik={null}
            numerSalwy={view.numerSalwy}
          />
        </Karta>
      ) : cel ? (
        <PlanszaSVG
          statki={cel.statki ?? []}
          strzaly={cel.strzaly}
          zatopione={cel.zatopione}
          pewnaWoda={cel.pewnaWoda}
          dozwolone={dozwolone}
          celownik={celownik}
          zlozoneStrzaly={naTejPlanszy}
          numerSalwy={view.numerSalwy}
          obrys={`var(--akcent-${cel.kolor})`}
          onPole={(pole) => {
            if (!dozwolone.has(pole)) return;
            setCelowniki({ ...celowniki, [cel.gracz]: pole });
            vibrate(15);
          }}
        />
      ) : (
        <Komunikat>Nie ma już w kogo strzelać.</Komunikat>
      )}

      {cel ? (
        <div className="text-sm text-tekst-slaby">
          Pozostałe statki: {cel.pozostaleRozmiary.join(", ") || "—"}
        </div>
      ) : null}

      <Tabela view={view} nicki={nicki} mojeId={mojeId} />

      {/* Stopka sterowania: miniatura własnej planszy i przycisk ognia. */}
      <div className="sticky bottom-0 -mx-4 border-t border-obrys bg-tlo px-4 pt-2 pb-2 flex items-center gap-3">
        <button
          onClick={() => setRozwinieta(!rozwinieta)}
          className="shrink-0 w-[110px] rounded-lg border border-obrys p-0.5"
          aria-label="Twoja plansza"
        >
          {/* Pola miniatury mają po 11 px i **nie są dotykalne** — to odczyt
              stanu, nie sterowanie, więc próg 44 px jej nie dotyczy. */}
          <PlanszaSVG
            wlasna
            miniatura
            statki={view.mojaPlansza?.statki ?? []}
            strzaly={view.mojaPlansza?.strzaly ?? []}
            zatopione={view.mojaPlansza?.zatopione ?? []}
            pewnaWoda={[]}
            dozwolone={null}
            celownik={null}
            numerSalwy={view.numerSalwy}
          />
        </button>

        <div className="flex-1 min-w-0 space-y-1">
          {zostalo > 0 ? (
            <Przycisk
              wariant="glowny"
              pelna
              disabled={celownik === null || !cel || view.odpadl}
              onClick={() => {
                if (cel && celownik !== null) {
                  onStrzel(cel.gracz, celownik);
                  // Celownik znika razem ze strzałem: pole ma od tej chwili
                  // własne oznaczenie, a przycisk pyta o kolejne pole zamiast
                  // proponować to samo, w które nie wolno już strzelić.
                  const bezCelownika = { ...celowniki };
                  delete bezCelownika[cel.gracz];
                  setCelowniki(bezCelownika);
                  vibrate([30, 40, 30]);
                }
              }}
            >
              {etykietaOgnia}
            </Przycisk>
          ) : (
            <Przycisk pelna disabled={zlozone.length === 0 || view.odpadl} onClick={onCofnijStrzal}>
              Zmień strzał
            </Przycisk>
          )}
          {/*
            Cofnąć da się w każdej chwili salwy, nie dopiero po wystrzelaniu
            wszystkiego: `cofnij-strzal` zdejmuje ostatni złożony strzał
            i sprawdza wyłącznie, czy jest co zdejmować.
          */}
          {zostalo > 0 && zlozone.length > 0 ? (
            <Przycisk wariant="cichy" pelna disabled={view.odpadl} onClick={onCofnijStrzal}>
              Cofnij ostatni strzał
            </Przycisk>
          ) : null}
          <div className="text-sm text-tekst-slaby tabular text-center">
            {view.iluWystrzelilo ?? 0}/{view.iluZywych ?? 0} wystrzeliło
            {limit > 1
              ? zostalo > 0
                ? ` · ${czasownikZostalo(zostalo)} jeszcze ${odmianaStrzalow(zostalo)}`
                : " · wszystkie strzały złożone"
              : ""}
          </div>
        </div>
      </div>
    </>
  );
}

/** Tabela na żywo — jedyne, co dostaje odpadnięty poza własną planszą. */
function Tabela({
  view,
  nicki,
  mojeId,
}: {
  view: StatkiView;
  nicki: Record<string, string>;
  mojeId: string;
}) {
  const tabela = view.tabela ?? [];
  if (tabela.length === 0) return null;

  return (
    <Karta>
      <div className="text-sm text-tekst-slaby mb-2">Na żywo</div>
      <div className="space-y-1">
        {tabela.map((wiersz) => (
          <div key={wiersz.gracz} className="flex items-center gap-2 text-sm">
            <span
              className="inline-block h-3 w-3 rounded-full shrink-0"
              style={{ backgroundColor: `var(--akcent-${view.kolory[wiersz.gracz] ?? "zielony"})` }}
            />
            <span className={`flex-1 truncate ${wiersz.zywa ? "" : "text-tekst-slaby"}`}>
              {nicki[wiersz.gracz] ?? wiersz.gracz}
              {wiersz.gracz === mojeId ? " (Ty)" : ""}
            </span>
            <span className="tabular text-tekst-slaby">
              {wiersz.trafionych}/{wiersz.oddanych}
            </span>
            <span className="tabular w-8 text-right">{wiersz.zatopil}⚓</span>
            <span className="tabular w-10 text-right">
              {wiersz.zywa ? `${wiersz.masztow}▮` : `${wiersz.padlaWSalwie ?? "?"}.`}
            </span>
          </div>
        ))}
      </div>
    </Karta>
  );
}

function Koniec({
  view,
  nicki,
  mojeId,
}: {
  view: StatkiView;
  nicki: Record<string, string>;
  mojeId: string;
}) {
  const punkty = view.punkty ?? {};
  const kolejnosc = [...view.players].sort((a, b) => (punkty[b] ?? 0) - (punkty[a] ?? 0));

  return (
    <>
      <Karta>
        <div className="text-sm text-tekst-slaby mb-3">Wynik po {view.numerSalwy} salwach</div>
        <div className="space-y-2">
          {kolejnosc.map((id, miejsce) => (
            <div key={id} className="flex items-center gap-3">
              <span className="w-6 text-tekst-slaby tabular">{miejsce + 1}.</span>
              <span className="flex-1 truncate">
                {nicki[id] ?? id}
                {id === mojeId ? " (Ty)" : ""}
              </span>
              <span className="tabular">{punkty[id] ?? 0}</span>
            </div>
          ))}
        </div>
      </Karta>

      <Komunikat>Wszystkie floty odkryte — to jest moment, dla którego się gra.</Komunikat>

      <Karta>
        <div className="text-sm text-tekst-slaby mb-2">Twoja plansza</div>
        <PlanszaSVG
          wlasna
          statki={view.mojaPlansza?.statki ?? []}
          strzaly={view.mojaPlansza?.strzaly ?? []}
          zatopione={view.mojaPlansza?.zatopione ?? []}
          pewnaWoda={[]}
          dozwolone={null}
          celownik={null}
          numerSalwy={view.numerSalwy}
        />
      </Karta>

      {(view.cele ?? []).map((cel) => (
        <Karta key={cel.gracz}>
          <div className="text-sm text-tekst-slaby mb-2">{nicki[cel.gracz] ?? cel.gracz}</div>
          <PlanszaSVG
            wlasna
            statki={cel.statki ?? []}
            strzaly={cel.strzaly}
            zatopione={cel.zatopione}
            pewnaWoda={[]}
            dozwolone={null}
            celownik={null}
            numerSalwy={view.numerSalwy}
            obrys={`var(--akcent-${cel.kolor})`}
          />
        </Karta>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Plansza
// ---------------------------------------------------------------------------

function odleglosc(dotyki: React.TouchList): number {
  const a = dotyki[0];
  const b = dotyki[1];
  if (!a || !b) return 0;
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

/**
 * Jedno SVG na planszę.
 *
 * Warstwy od dołu: woda i siatka, pewna woda, pudła, kadłuby zatopionych,
 * trafienia, własne strzały tej salwy, celownik, ozdoby (podpisy A–J i 1–10).
 * **Cała treść ma
 * `pointerEvents: "none"`**, a dotknięcia zbiera osobna, przezroczysta warstwa
 * pól na samej górze. Bez tego powtórzylibyśmy błąd z Catanu, gdzie tekstura
 * heksa przechwyciła dotknięcie i złodzieja nie dało się przestawić na żadne
 * pole — a tutaj dekoracyjny obrys kadłuba leży dokładnie nad polami, w które
 * trzeba strzelać.
 *
 * Rysunek jest kodem, nie plikiem rastrowym: największy element to pole 36 px,
 * a najczęstszy — kropka pudła o średnicy 10 px. Zdjęcie eksplozji w tej skali
 * jest plamą, a `<circle>` rysuje się za darmo i zostaje ostry przy 3×.
 */
function PlanszaSVG({
  statki,
  strzaly,
  zatopione,
  pewnaWoda,
  dozwolone,
  celownik,
  zlozoneStrzaly = [],
  numerSalwy,
  onPole,
  wlasna = false,
  miniatura = false,
  obrys,
}: {
  statki: StatekWidok[];
  strzaly: StrzalWidok[];
  zatopione: number[];
  pewnaWoda: number[];
  /** `null` = plansza tylko do oglądania. */
  dozwolone: Set<number> | null;
  celownik: number | null;
  /** Własne strzały tej salwy, jeszcze nierozliczone, z numerem kolejności. */
  zlozoneStrzaly?: Array<{ pole: number; numer: number }>;
  numerSalwy: number;
  onPole?: (pole: number) => void;
  wlasna?: boolean;
  miniatura?: boolean;
  obrys?: string;
}) {
  const [skala, setSkala] = useState(1);
  const [przesuniecie, setPrzesuniecie] = useState({ x: 0, y: 0 });
  const gest = useRef<{
    startOdleglosc: number;
    startSkala: number;
    startPunkt: { x: number; y: number };
    startPrzesuniecie: { x: number; y: number };
    przesuwanie: boolean;
  } | null>(null);

  const onTouchStart = (event: React.TouchEvent) => {
    if (miniatura) return;
    if (event.touches.length === 2) {
      gest.current = {
        startOdleglosc: odleglosc(event.touches),
        startSkala: skala,
        startPunkt: { x: 0, y: 0 },
        startPrzesuniecie: przesuniecie,
        przesuwanie: false,
      };
    } else if (event.touches.length === 1 && skala > 1) {
      const dotyk = event.touches[0]!;
      gest.current = {
        startOdleglosc: 0,
        startSkala: skala,
        startPunkt: { x: dotyk.clientX, y: dotyk.clientY },
        startPrzesuniecie: przesuniecie,
        przesuwanie: true,
      };
    }
  };

  const onTouchMove = (event: React.TouchEvent) => {
    const stan = gest.current;
    if (!stan) return;
    if (event.touches.length === 2 && stan.startOdleglosc > 0) {
      const nowa = Math.min(
        MAX_SKALA,
        Math.max(1, (stan.startSkala * odleglosc(event.touches)) / stan.startOdleglosc),
      );
      setSkala(nowa);
      if (nowa === 1) setPrzesuniecie({ x: 0, y: 0 });
    } else if (stan.przesuwanie && event.touches.length === 1) {
      const dotyk = event.touches[0]!;
      const limit = (skala - 1) * 50;
      setPrzesuniecie({
        x: Math.max(
          -limit,
          Math.min(limit, stan.startPrzesuniecie.x + (dotyk.clientX - stan.startPunkt.x) / skala),
        ),
        y: Math.max(
          -limit,
          Math.min(limit, stan.startPrzesuniecie.y + (dotyk.clientY - stan.startPunkt.y) / skala),
        ),
      });
    }
  };

  const onTouchEnd = (event: React.TouchEvent) => {
    if (event.touches.length === 0) gest.current = null;
  };

  const trafione = new Map(strzaly.map((strzal) => [strzal.pole, strzal]));
  const kadluby = new Set(zatopione);
  const woda = new Set(pewnaWoda);
  const swieze = new Set(
    strzaly.filter((strzal) => strzal.salwa === numerSalwy - 1).map((strzal) => strzal.pole),
  );
  const drgnij = [...swieze].some((pole) => trafione.get(pole)?.wynik !== "pudlo");

  /** Lewy górny róg pola w jednostkach viewBox; 10 jednostek zajmują podpisy. */
  const x = (pole: number) => 10 + (pole % BOK) * 10;
  const y = (pole: number) => 10 + Math.floor(pole / BOK) * 10;

  return (
    <div className={miniatura ? "" : "mx-auto w-full max-w-[62vh] relative"}>
      {!miniatura && skala > 1 ? (
        <button
          onClick={() => {
            setSkala(1);
            setPrzesuniecie({ x: 0, y: 0 });
          }}
          className="absolute -top-1 right-0 z-10 text-xs bg-panel-jasny border border-obrys rounded-lg px-2 py-1 text-tekst-slaby"
        >
          {skala.toFixed(1)}× · wróć
        </button>
      ) : null}

      <div
        className="overflow-hidden rounded-lg"
        style={{ touchAction: miniatura ? "pan-y" : skala > 1 ? "none" : "pan-y" }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <svg
          viewBox="0 0 111 111"
          className={`w-full select-none ${drgnij && !miniatura ? "statki-drgniecie" : ""}`}
          key={`plansza:${numerSalwy}`}
          style={{
            transform: `scale(${skala}) translate(${przesuniecie.x}%, ${przesuniecie.y}%)`,
            transition: gest.current ? "none" : "transform 150ms ease-out",
          }}
        >
          {/* 1. woda i siatka */}
          <g style={{ pointerEvents: "none" }}>
            <rect x={10} y={10} width={100} height={100} fill="var(--morze)" rx={1} />
            {Array.from({ length: BOK + 1 }, (_, i) => (
              <g key={`siatka:${i}`}>
                <line
                  x1={10 + i * 10}
                  y1={10}
                  x2={10 + i * 10}
                  y2={110}
                  stroke="var(--morze-siatka)"
                  strokeWidth={0.3}
                />
                <line
                  x1={10}
                  y1={10 + i * 10}
                  x2={110}
                  y2={10 + i * 10}
                  stroke="var(--morze-siatka)"
                  strokeWidth={0.3}
                />
              </g>
            ))}
          </g>

          {/* 2. własna flota — wyłącznie na własnej planszy */}
          {wlasna ? (
            <g style={{ pointerEvents: "none" }}>
              {statki.map((statek) =>
                statek.pola.map((pole, i) => (
                  <rect
                    key={`statek:${pole}`}
                    x={x(pole) + 1}
                    y={y(pole) + 1}
                    width={8}
                    height={8}
                    rx={1.5}
                    fill={statek.trafienia[i] ? "var(--statek-trafiony)" : "var(--statek)"}
                  />
                )),
              )}
            </g>
          ) : null}

          {/* 3. pewna woda wokół zatopionych */}
          <g style={{ pointerEvents: "none" }}>
            {[...woda].map((pole) => (
              <circle
                key={`woda:${pole}`}
                cx={x(pole) + 5}
                cy={y(pole) + 5}
                r={1}
                fill="var(--pewna-woda)"
              />
            ))}
          </g>

          {/* 4. kadłuby zatopionych — pełny obrys, bo statek jest już jawny */}
          <g style={{ pointerEvents: "none" }}>
            {[...kadluby].map((pole) => (
              <rect
                key={`kadlub:${pole}`}
                className={swieze.has(pole) ? "statki-kadlub" : ""}
                x={x(pole) + 0.6}
                y={y(pole) + 0.6}
                width={8.8}
                height={8.8}
                rx={1.5}
                fill="var(--zatopiony)"
              />
            ))}
          </g>

          {/* 5. pudła i trafienia */}
          <g style={{ pointerEvents: "none" }}>
            {strzaly.map((strzal) =>
              strzal.wynik === "pudlo" ? (
                <circle
                  key={`strzal:${strzal.pole}`}
                  className={swieze.has(strzal.pole) ? "statki-plusk" : ""}
                  cx={x(strzal.pole) + 5}
                  cy={y(strzal.pole) + 5}
                  r={1.8}
                  fill="var(--pudlo)"
                />
              ) : (
                <g key={`strzal:${strzal.pole}`}>
                  <line
                    x1={x(strzal.pole) + 2.5}
                    y1={y(strzal.pole) + 2.5}
                    x2={x(strzal.pole) + 7.5}
                    y2={y(strzal.pole) + 7.5}
                    stroke="var(--trafienie)"
                    strokeWidth={1.4}
                    strokeLinecap="round"
                  />
                  <line
                    x1={x(strzal.pole) + 7.5}
                    y1={y(strzal.pole) + 2.5}
                    x2={x(strzal.pole) + 2.5}
                    y2={y(strzal.pole) + 7.5}
                    stroke="var(--trafienie)"
                    strokeWidth={1.4}
                    strokeLinecap="round"
                  />
                  {swieze.has(strzal.pole) ? (
                    <circle
                      className="statki-pierscien"
                      cx={x(strzal.pole) + 5}
                      cy={y(strzal.pole) + 5}
                      r={2}
                      fill="none"
                      stroke="var(--trafienie)"
                      strokeWidth={0.8}
                    />
                  ) : null}
                </g>
              ),
            )}
          </g>

          {/*
            6. własne strzały tej salwy — trzeci stan pola.
            Rozliczone trafienie to krzyż, pudło kropka, celownik pełna ramka;
            ten stan dostaje **ramkę przerywaną z numerem kolejności**, więc
            rozróżnia go kształt, nie sam odcień. Numer jest tu jedyną
            informacją, której nie da się odczytać z planszy: przy trzech
            strzałach „ten poszedł drugi" decyduje, co cofnie „Cofnij".
          */}
          <g style={{ pointerEvents: "none" }}>
            {zlozoneStrzaly.map((wpis) => (
              <g key={`zlozony:${wpis.pole}`}>
                <rect
                  x={x(wpis.pole) + 0.7}
                  y={y(wpis.pole) + 0.7}
                  width={8.6}
                  height={8.6}
                  rx={1.5}
                  fill="var(--strzal-zlozony)"
                  fillOpacity={0.18}
                  stroke="var(--strzal-zlozony)"
                  strokeWidth={1}
                  strokeDasharray="2 1.4"
                />
                {miniatura ? null : (
                  <text
                    x={x(wpis.pole) + 5}
                    y={y(wpis.pole) + 7}
                    textAnchor="middle"
                    fontSize={6}
                    fontWeight={700}
                    fill="var(--strzal-zlozony)"
                  >
                    {wpis.numer}
                  </text>
                )}
              </g>
            ))}
          </g>

          {/* 7. celownik */}
          {celownik !== null ? (
            <rect
              className="cel-pulsuje"
              style={{ pointerEvents: "none" }}
              x={x(celownik) + 0.7}
              y={y(celownik) + 0.7}
              width={8.6}
              height={8.6}
              rx={1.5}
              fill="none"
              stroke="var(--celownik)"
              strokeWidth={1.4}
            />
          ) : null}

          {/* 8. ozdoby: ramka i podpisy A–J oraz 1–10 */}
          <g style={{ pointerEvents: "none" }}>
            {obrys ? (
              <rect
                x={10}
                y={10}
                width={100}
                height={100}
                rx={1}
                fill="none"
                stroke={obrys}
                strokeWidth={1}
              />
            ) : null}
            {Array.from({ length: BOK }, (_, i) => (
              <text
                key={`wiersz:${i}`}
                x={5}
                y={16 + i * 10}
                textAnchor="middle"
                fontSize={5}
                fill="var(--plansza-tekst)"
              >
                {String.fromCharCode(65 + i)}
              </text>
            ))}
            {Array.from({ length: BOK }, (_, i) => (
              <text
                key={`kolumna:${i}`}
                x={15 + i * 10}
                y={7}
                textAnchor="middle"
                fontSize={5}
                fill="var(--plansza-tekst)"
              >
                {i + 1}
              </text>
            ))}
          </g>

          {/*
            Warstwa dotyku, zawsze na samej górze. Pola dozwolone dostają lekki
            połysk; reszta jest przezroczysta i nieklikalna.
          */}
          {dozwolone !== null && onPole ? (
            <g>
              {Array.from({ length: BOK * BOK }, (_, pole) =>
                dozwolone.has(pole) ? (
                  <rect
                    key={`dotyk:${pole}`}
                    x={x(pole)}
                    y={y(pole)}
                    width={10}
                    height={10}
                    fill="var(--celownik)"
                    fillOpacity={0.12}
                    onClick={() => onPole(pole)}
                  />
                ) : (
                  <rect
                    key={`dotyk:${pole}`}
                    x={x(pole)}
                    y={y(pole)}
                    width={10}
                    height={10}
                    fill="none"
                    style={{ pointerEvents: "all" }}
                    onClick={() => onPole(pole)}
                  />
                ),
              )}
            </g>
          ) : null}
        </svg>
      </div>
    </div>
  );
}
